import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import { aboutInfo } from '@/mocks/content';

export default function AboutPage() {
  const organizationLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'yy6080',
    url: 'https://dramaslab.github.io',
    description: 'yy6080是一个深受用户喜爱的在线视频平台，提供海量高清影视资源，包括电影、电视剧、动漫等内容，无需注册即可免费观看。',
    email: aboutInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
    },
  };

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaslab.github.io/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://dramaslab.github.io/about' },
    ],
  };

  const seoTitle = '关于我们 - yy6080品牌介绍与联系方式 | yy6080在线观看';
  const seoDesc = '了解yy6080品牌故事、发展历程与核心团队。yy6080致力于为全球华语用户提供免费高清在线视频服务，海量电影、电视剧、动漫资源。联系我们获取更多信息。';

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords="yy6080,yy6080在线观看,关于yy6080,yy6080联系方式"
        canonicalUrl="https://dramaslab.github.io/about"
      />
      <StructuredData data={breadcrumbLD} />
      <StructuredData data={organizationLD} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <div className="px-4 md:px-6 py-6 w-full">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">
                关于我们
              </h1>

              {/* Brand Introduction */}
              <section className="mb-14">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-building-2-line text-primary-500"></i>
                  品牌介绍
                </h2>
                <div className="prose max-w-none">
                  {aboutInfo.brandIntro.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-sm text-foreground-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
                  {[
                    { icon: 'ri-eye-line', title: '我们的愿景', desc: '让每个人都能免费享受高品质影视内容' },
                    { icon: 'ri-heart-pulse-line', title: '我们的使命', desc: '打造最优质的华语在线视频平台' },
                    { icon: 'ri-shield-star-line', title: '核心价值观', desc: '用户至上、内容为王、技术驱动' },
                  ].map((item, idx) => (
                    <div key={idx} className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1.5 whitespace-nowrap">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Reputation */}
              <section className="mb-14">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-chat-heart-line text-primary-500"></i>
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {aboutInfo.reputation.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={item.avatar}
                          alt={item.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                            {item.name}
                          </h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.comment}</p>
                          <div className="flex items-center gap-0.5 mt-3">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="mb-10">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-mail-line text-primary-500"></i>
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4 whitespace-nowrap">联系方式</h3>
                    <div className="space-y-3">
                      <ContactItem icon="ri-mail-line" label="电子邮箱" value={aboutInfo.contact.email} />
                      <ContactItem icon="ri-wechat-line" label="微信公众号" value={aboutInfo.contact.wechat} />
                      <ContactItem icon="ri-qq-line" label="QQ群" value={aboutInfo.contact.qq} />
                      <ContactItem icon="ri-map-pin-line" label="公司地址" value={aboutInfo.contact.address} />
                    </div>
                  </div>

                  <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4 whitespace-nowrap">在线留言</h3>
                    <ContactForm />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function ContactItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 flex-shrink-0">
        <i className={icon}></i>
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-foreground-500 whitespace-nowrap">{label}：</span>
        <span className="text-foreground-800 break-all">{value}</span>
      </div>
    </div>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    const formDataObj = new FormData(form);
    const honeypotVal = (formDataObj.get('website_alt') as string || '').trim();
    if (honeypotVal) {
      setStatus('success');
      setStatusMsg('留言已提交，感谢您的反馈！');
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus('error');
      setStatusMsg('请填写所有必填字段');
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setStatusMsg('留言内容不能超过500个字符');
      return;
    }

    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name.trim());
      payload.append('email', formData.email.trim());
      payload.append('message', formData.message.trim());

      const response = await fetch('https://readdy.ai/api/form/d9u3bjlca23r0tr1apng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string)
        || (parsed?.meta as Record<string, unknown>)?.detail as string
        || '';

      if (response.ok && (parsed?.code === 'OK')) {
        setStatus('success');
        setStatusMsg('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
        form.reset();
      } else {
        setStatus('error');
        setStatusMsg(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络异常，请稍后重试');
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的姓名"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入留言内容（最多500字）"
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          maxLength={500}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>
      <div className="honeypot-field">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <button
        type="submit"
        className="w-full px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line mr-2"></i>发送留言
      </button>
      {status !== 'idle' && (
        <p className={`text-xs ${status === 'success' ? 'text-accent-600' : 'text-primary-600'}`}>
          {statusMsg}
        </p>
      )}
    </form>
  );
}